/**
 * Shared validation and calculation for hierarchical product units.
 *
 * `factor` remains the absolute multiplier to the product's smallest unit so
 * existing stock movements and documents keep their historical meaning. The
 * parent relation is the operator-facing representation used to calculate it.
 */

export const MAX_UNIT_FACTOR = 1_000_000;

export interface UnitHierarchyInput {
  id?: string;
  unitName: string;
  factor?: number;
  parentUnitId?: string | null;
  unitsPerParent?: number | null;
  sortOrder?: number;
  active?: boolean;
  [key: string]: unknown;
}

export interface ResolvedUnitHierarchy extends UnitHierarchyInput {
  id: string;
  factor: number;
  parentUnitId: string | null;
  unitsPerParent: number | null;
  sortOrder: number;
}

export class UnitHierarchyError extends Error {
  constructor(
    public readonly code: string,
    public readonly path: string,
    message: string,
  ) {
    super(message);
    this.name = "UnitHierarchyError";
  }
}

function fail(code: string, path: string, message: string): never {
  throw new UnitHierarchyError(code, path, message);
}

function positiveInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

function unitKey(value: string) {
  return value.trim().toLocaleLowerCase();
}

/**
 * Resolve a product's units in array order. The first row is always the
 * smallest unit; every later row may reference only an earlier row.
 *
 * Payloads from older clients can omit the parent fields and provide only an
 * absolute `factor`. In that case the nearest preceding divisor is inferred.
 */
export function resolveUnitHierarchy<T extends UnitHierarchyInput>(
  input: readonly T[],
  baseUnit: string,
): Array<T & ResolvedUnitHierarchy> {
  if (!input.length) {
    fail("UNIT_LIST_EMPTY", "units", "产品至少需要一个单位。");
  }

  const trimmedBase = baseUnit.trim();
  if (!trimmedBase) fail("BASE_UNIT_REQUIRED", "baseUnit", "请填写最小单位。");

  const ids = new Set<string>();
  const names = new Set<string>();
  const byId = new Map<string, ResolvedUnitHierarchy>();
  const resolved: Array<T & ResolvedUnitHierarchy> = [];

  for (const [index, raw] of input.entries()) {
    const name = raw.unitName.trim();
    const path = `units.${index}`;
    if (!name) fail("UNIT_NAME_REQUIRED", `${path}.unitName`, "请填写单位名称。");

    const nameKey = unitKey(name);
    if (names.has(nameKey)) {
      fail("DUPLICATE_UNIT", `${path}.unitName`, "同一产品不能重复维护相同单位。");
    }
    names.add(nameKey);

    const id = raw.id ?? crypto.randomUUID();
    if (ids.has(id)) fail("DUPLICATE_UNIT_ID", `${path}.id`, "单位标识不能重复。");
    ids.add(id);

    const sortOrder = index + 1;
    if (index === 0) {
      if (unitKey(name) !== unitKey(trimmedBase)) {
        fail("BASE_UNIT_REQUIRED", `${path}.unitName`, "第一行必须是产品的最小单位。");
      }
      if (raw.parentUnitId !== undefined && raw.parentUnitId !== null) {
        fail("BASE_UNIT_PARENT_INVALID", `${path}.parentUnitId`, "最小单位不能有父单位。");
      }
      if (raw.unitsPerParent !== undefined && raw.unitsPerParent !== null) {
        fail("BASE_UNIT_PARENT_INVALID", `${path}.unitsPerParent`, "最小单位不能填写父单位数量。");
      }
      if (raw.factor !== undefined && raw.factor !== 1) {
        fail("BASE_UNIT_FACTOR_INVALID", `${path}.factor`, "最小单位的累计换算必须为 1。");
      }
      const base = {
        ...raw,
        id,
        unitName: name,
        factor: 1,
        parentUnitId: null,
        unitsPerParent: null,
        sortOrder,
      } as T & ResolvedUnitHierarchy;
      resolved.push(base);
      byId.set(id, base);
      continue;
    }

    const explicitParent = raw.parentUnitId !== undefined || raw.unitsPerParent !== undefined;
    let factor: number;
    let parentUnitId: string | null;
    let unitsPerParent: number | null;

    if (explicitParent) {
      if (!raw.parentUnitId) {
        fail("UNIT_PARENT_REQUIRED", `${path}.parentUnitId`, "请选择已有单位作为父单位。");
      }
      const parent = byId.get(raw.parentUnitId);
      if (!parent) {
        fail(
          "UNIT_PARENT_ORDER_INVALID",
          `${path}.parentUnitId`,
          "父单位必须是当前行之前已经填写的单位。",
        );
      }
      if (!positiveInteger(raw.unitsPerParent) || raw.unitsPerParent < 2) {
        fail("UNIT_RATIO_INVALID", `${path}.unitsPerParent`, "相对数量必须是大于等于 2 的正整数。");
      }
      factor = parent.factor * raw.unitsPerParent;
      parentUnitId = raw.parentUnitId;
      unitsPerParent = raw.unitsPerParent;
      if (factor > MAX_UNIT_FACTOR) {
        fail(
          "UNIT_FACTOR_TOO_LARGE",
          `${path}.unitsPerParent`,
          `累计换算不能超过 ${MAX_UNIT_FACTOR.toLocaleString()}。`,
        );
      }
      if (raw.factor !== undefined && raw.factor !== factor) {
        fail(
          "UNIT_FACTOR_MISMATCH",
          `${path}.factor`,
          "累计换算与父单位和相对数量不一致，请刷新后重试。",
        );
      }
    } else {
      if (!positiveInteger(raw.factor) || raw.factor > MAX_UNIT_FACTOR || raw.factor < 2) {
        fail(
          "UNIT_FACTOR_INVALID",
          `${path}.factor`,
          `旧格式累计换算必须是 2 至 ${MAX_UNIT_FACTOR.toLocaleString()} 的正整数。`,
        );
      }
      factor = raw.factor;
      const parent = [...resolved]
        .filter((candidate) => candidate.factor < factor && factor % candidate.factor === 0)
        .sort((left, right) => right.factor - left.factor || left.sortOrder - right.sortOrder)[0];
      if (!parent) {
        fail(
          "UNIT_PARENT_REQUIRED",
          `${path}.parentUnitId`,
          "无法从旧累计换算推导父单位，请选择已有父单位。",
        );
      }
      parentUnitId = parent.id;
      unitsPerParent = factor / parent.factor;
    }

    if (resolved.some((candidate) => candidate.factor === factor)) {
      fail("DUPLICATE_UNIT_FACTOR", `${path}.unitsPerParent`, "同一产品不能重复使用相同累计换算。");
    }

    const unit = {
      ...raw,
      id,
      unitName: name,
      factor,
      parentUnitId,
      unitsPerParent,
      sortOrder,
    } as T & ResolvedUnitHierarchy;
    resolved.push(unit);
    byId.set(id, unit);
  }

  return resolved;
}

export interface ComparableUnit {
  id?: string | null;
  unitName: string;
  factor?: number | null;
  factorMilli?: number | null;
  parentUnitId?: string | null;
  unitsPerParent?: number | null;
  sortOrder?: number | null;
  active?: boolean | number | null;
}

/** Return a stable representation suitable for detecting conversion edits. */
export function unitConversionFingerprint(units: readonly ComparableUnit[]) {
  return JSON.stringify(
    units
      .map((unit) => ({
        id: unit.id ?? null,
        unitName: unitKey(unit.unitName),
        factor:
          unit.factor ??
          (unit.factorMilli === null || unit.factorMilli === undefined
            ? null
            : Number(unit.factorMilli) / 1000),
        parentUnitId: unit.parentUnitId ?? null,
        unitsPerParent: unit.unitsPerParent ?? null,
        sortOrder: unit.sortOrder ?? null,
        active: Boolean(unit.active),
      }))
      .sort((left, right) =>
        String(left.id ?? left.unitName).localeCompare(String(right.id ?? right.unitName)),
      ),
  );
}

export function unitConversionChanged(
  before: readonly ComparableUnit[],
  after: readonly ComparableUnit[],
) {
  return unitConversionFingerprint(before) !== unitConversionFingerprint(after);
}
